import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LabOrder } from "../../interfaces/LabOrder";

import { getLabOrderById, printLabReport } from "../../apis/LabOrder";
import { useNotification } from "../../NotificationContext";
import { Tag } from "antd";
import { LabOrderStatus } from "../../interfaces/LabOrder";

interface ViewLabOrderProps {
  isModal?: boolean;
  patientId?: number | string;
  orderId?: number | null;
  onClose?: () => void;
}

const getStatusColor = (status?: LabOrderStatus) => {
  switch (status) {
    case "ordered":
      return "red";
    case "in_progress":
      return "blue";
    case "completed":
      return "green";
    // case "cancelled":
    //   return "default";
    default:
      return "default";
  }
};

const ViewLabOrder: React.FC<ViewLabOrderProps> = ({ isModal, patientId, onClose,orderId }) => {
// const ViewLabOrder: React.FC = () => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  // const { id } = useParams<{ id: string }>();
  // const orderId = id ? parseInt(id) : 0;
  const params = useParams<{ id: string }>();
  const finalOrderId =
  orderId ?? (params.id ? parseInt(params.id) : null);
  const [order, setOrder] = useState<LabOrder | null>(null);
  const [isFetching, setIsFetching] = useState(true);
  const [testResults, setTestResults] = useState<{ [key: string]: string }>({});
  const [labOrders, setLabOrders] = useState<any[]>([]);

useEffect(() => {
  if (finalOrderId) {
    fetchOrderDetails(finalOrderId);
  }
}, [finalOrderId]);

  const fetchOrderDetails = async (id:number) => {
    try {
      setIsFetching(true);
      const res = await getLabOrderById(id);
      const orderData = res.data.data;
      setOrder(orderData);
      
      // Initialize test results from existing data
      // API structure: sections are at root level, not in items
      const results: { [key: string]: string } = {};
      
      // Check if sections are at root level (new API structure)
      if (orderData?.sections && orderData.sections.length > 0) {
        orderData.sections.forEach((section: any) => {
          if (section.parameters && section.parameters.length > 0) {
            section.parameters.forEach((param: any) => {
              // Use order ID and parameter ID for the key
              const key = `${orderData.id}-${param.id}`;
              // Result can be an object with value property or a string
              results[key] = param.result?.value || param.result || "";
            });
          }
        });
      }
      // Fallback: Check if items exist (old structure)
      else if (orderData?.items) {
        orderData.items.forEach((item: any) => {
          if (item.sections && item.sections.length > 0) {
            item.sections.forEach((section: any) => {
              if (section.parameters && section.parameters.length > 0) {
                section.parameters.forEach((param: any) => {
                  const key = `${item.id}-${param.id}`;
                  results[key] = param.result?.value || param.result || "";
                });
              }
            });
          }
          else if (item.parameters && item.parameters.length > 0) {
            item.parameters.forEach((param: any) => {
              const key = `${item.id}-${param.id}`;
              results[key] = param.result || param.value || "";
            });
          }
        });
      }
      
      setTestResults(results);
    } catch (err) {
      console.error("Error fetching order details:", err);
      showNotification("error", "Failed to load order details", "", "topRight");
    } finally {
      setIsFetching(false);
    }
  };

  const handlePrintReport = async () => {
    if (!order?.id) return;
    try {
      const res = await printLabReport(order.id);
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "text/html" }));
      window.open(blobUrl, "_blank");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      showNotification("error", "Failed to print report", "", "topRight");
    }
  };

  if (isFetching) {
    return (
      <div className="p-6 max-w-6xl">
        <div className="text-center">Loading order details...</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="p-6 max-w-6xl">
        <div className="text-center text-red-600">Order not found</div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6">
      
      {!isModal && (
      <div className="mb-6">
        <h1 className="text-lg font-semibold text-gray-900">
          Lab Order Details
        </h1>
        <p className="mt-1 text-sm font-medium text-gray-900 sm:text-base">
          Order Number - {order.orderNumber}
        </p>
        <p className="mt-1 text-sm font-medium text-gray-900 capitalize sm:text-base">
          Patient Name - {order.patient
            ? `${order.patient.firstName || ""} ${order.patient.lastName || ""}`.trim()
            : `Patient #${order.patientId}`}
        </p>
        <p className="mt-1 text-sm font-medium text-gray-900 sm:text-base">
          Sample Id - {order.sampleId}
        </p>

        <div className="flex gap-3 justify-end items-center border-gray-200">
        <button
          type="button"
          className="px-6 py-2 text-sm font-semibold text-white rounded-md border border-gray-300 bg-primary"
          onClick={handlePrintReport}
        >
          Print
        </button>
        </div>
      </div>
      )}
     
      {isModal && order.status && (
        <div className="flex justify-end mb-4">
          <Tag color={getStatusColor(order.status as LabOrderStatus)}>
            {order.status?.toUpperCase()}
          </Tag>
        </div>
      )}

      {/* Tests Section */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        {(() => {
          // Handle new API structure: sections at root level, single lab_test
          const orderData = order as any;
          const sections = orderData?.sections || [];
          const labTest = orderData?.lab_test || orderData?.labTest;
          const testName = labTest?.name || "Unknown Test";
          
          // If sections exist at root level (new structure)
          if (sections.length > 0) {
            return (
              <div>
                {/* Test Header */}
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <h3 className="text-base font-semibold text-center text-gray-900 uppercase">
                    {testName}
                  </h3>
                </div>

                {/* Sections and Parameters - Single Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-xs font-semibold tracking-wider text-left text-gray-700 uppercase border-r border-gray-300">
                          
                        </th>
                        <th className="px-4 py-2 text-xs font-semibold tracking-wider text-left text-gray-700 uppercase border-r border-gray-300">
                          Result
                        </th>
                        <th className="px-4 py-2 text-xs font-semibold tracking-wider text-left text-gray-700 uppercase border-r border-gray-300">
                          Unit
                        </th>
                        <th className="px-4 py-2 text-xs font-semibold tracking-wider text-left text-gray-700 uppercase">
                          Reference Value
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {/* {sections.map((section: any, sectionIndex: number) => { */}
                      {[...sections]
                        .sort((a: any, b: any) => a.displayOrder - b.displayOrder)
                        .map((section: any, sectionIndex: number) => {
                        // const sectionParams = section.parameters || [];
                          const sectionParams = [...(section.parameters || [])]
                            .sort((a: any, b: any) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
                        if (sectionParams.length === 0) return null;

                        return (
                          <React.Fragment key={section.id || sectionIndex}>
                            {/* Section Header Row */}
                            <tr className="bg-gray-100">
                              <td 
                                colSpan={4}
                                className="px-4 py-2 text-sm font-semibold text-gray-900 uppercase border-b border-gray-300"
                              >
                                {section.name}
                              </td>
                            </tr>
                            {/* Parameters Rows */}
                            {sectionParams.map((param: any, paramIndex: number) => {
                              const key = `${orderData.id}-${param.id}`;
                              // Get result value - can be from state, param.result.value, or param.result
                              const resultValue = testResults[key] || param.result?.value || param.result || "";
                              
                              // Format reference ranges to match ViewLabTest format
                              const formatReferenceValue = () => {
                                console.log("Parameter:", param);

                                if (param.referenceRanges && param.referenceRanges.length > 0) {
                                  // Display all reference ranges in structured format
                                  return param.referenceRanges.map((range: any, rangeIndex: number) => {
                                    const rangeMin = range.rangeMin;
                                    const rangeMax = range.rangeMax;
                                    const categoryName = range.categoryName;
                                    const ageMin = range.ageMin;
                                    const ageMax = range.ageMax;
                                    const gender = range.gender;
                                    
                                    // Check if both rangeMin and rangeMax are not available
                                    const hasMin = rangeMin !== null && rangeMin !== undefined && rangeMin !== "";
                                    const hasMax = rangeMax !== null && rangeMax !== undefined && rangeMax !== "";
                                    
                                    if (!hasMin && !hasMax) {
                                      // If both are not available, show Absent
                                      return (
                                        <div key={rangeIndex} className="text-sm text-gray-700">
                                          Absent
                                        </div>
                                      );
                                    }
                                    
                                    // Format range values with 2 decimal places
                                    const formatRangeValue = (value: any) => {
                                      if (value === null || value === undefined || value === "") return "";
                                      const numValue = Number(value);
                                      if (isNaN(numValue)) return value;
                                      return numValue.toFixed(2);
                                    };
                                    
                                    // Build age and gender info string
                                    const buildAgeGenderInfo = () => {
                                      const hasAgeMin = ageMin !== null && ageMin !== undefined && ageMin !== "";
                                      const hasAgeMax = ageMax !== null && ageMax !== undefined && ageMax !== "";
                                      
                                      if (hasAgeMin || hasAgeMax) {
                                        const ageStr = hasAgeMin && hasAgeMax 
                                          ? `${ageMin} - ${ageMax}`
                                          : hasAgeMin 
                                          ? `≥ ${ageMin}`
                                          : `≤ ${ageMax}`;
                                        
                                        const genderStr = gender ? ` (${gender})` : "";
                                        return ` (Age: ${ageStr}${genderStr})`;
                                      } else if (gender) {
                                        return ` (${gender})`;
                                      }
                                      
                                      return "";
                                    };
                                    
                                    const ageGenderInfo = buildAgeGenderInfo();
                                    
                                    if (hasMin && hasMax) {
                                      // Range format: "Category: min - max (Age: ageMin - ageMax (gender))"
                                      const formattedMin = formatRangeValue(rangeMin);
                                      const formattedMax = formatRangeValue(rangeMax);
                                      return (
                                        <div key={rangeIndex} className="text-sm text-gray-700">
                                          {categoryName ? `${categoryName}: ` : ""}{formattedMin} - {formattedMax}{ageGenderInfo}
                                        </div>
                                      );
                                    } else if (hasMin) {
                                      // Minimum only: "Category: => min (Age: ageMin - ageMax (gender))"
                                      const formattedMin = formatRangeValue(rangeMin);
                                      return (
                                        <div key={rangeIndex} className="text-sm text-gray-700">
                                          {categoryName ? `${categoryName}: ` : ""}≥ {formattedMin}{ageGenderInfo}
                                        </div>
                                      );
                                    } else if (hasMax) {
                                      // Maximum only: "Category: < max (Age: ageMin - ageMax (gender))"
                                      const formattedMax = formatRangeValue(rangeMax);
                                      return (
                                        <div key={rangeIndex} className="text-sm text-gray-700">
                                          {categoryName ? `${categoryName}: ` : ""}≤ {formattedMax}{ageGenderInfo}
                                        </div>
                                      );
                                    }
                                    return null;
                                  });
                                } else {
                                  // Fallback to old format for backward compatibility
                                  const refText = param.reference_text || param.referenceText;
                                  const refMin = param.reference_min || param.referenceMin;
                                  const refMax = param.reference_max || param.referenceMax;
                                  
                                  // Check if both refMin and refMax are not available
                                  const hasMin = refMin !== null && refMin !== undefined && refMin !== "";
                                  const hasMax = refMax !== null && refMax !== undefined && refMax !== "";
                                  
                                  if (!hasMin && !hasMax) {
                                    // If both are not available, show Absent
                                    return <div className="text-sm text-gray-700">Absent</div>;
                                  }
                                  
                                  if (refText) {
                                    return <div className="text-sm text-gray-700">{refText}</div>;
                                  } else if (hasMin && hasMax) {
                                    return <div className="text-sm text-gray-700">{refMin} - {refMax}</div>;
                                  } else if (hasMin) {
                                    return <div className="text-sm text-gray-700">≥ {refMin}</div>;
                                  } else if (hasMax) {
                                    return <div className="text-sm text-gray-700">≤ {refMax}</div>;
                                  }
                                  return <div className="text-sm text-gray-700">Absent</div>;
                                }
                              };

                              return (
                                <tr key={param.id || `${section.id}-${paramIndex}`} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 text-sm font-medium text-gray-900 capitalize border-r border-gray-300">
                                    {param.name || "-"}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-900 border-r border-gray-300">
                                    {resultValue || "-"}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-300">
                                    {param.unit || "-"}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-700 align-top">
                                    <div className="space-y-0.5">
                                      {formatReferenceValue()}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          }
          
          // Fallback: Handle old structure with items array
          if (order.items && order.items.length > 0) {
            return order.items.map((item: any, itemIndex: number) => {
              const itemTestName = item.lab_test?.name || item.labTest?.name || item.name || "Unknown Test";
              const itemSections = item.sections || [];

              return (
                <div key={itemIndex} className={itemIndex > 0 ? "border-t border-gray-200" : ""}>
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <h3 className="text-base font-semibold text-gray-900 uppercase">
                      {itemTestName}
                    </h3>
                  </div>
                  {itemSections.length > 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      Sections found in items (old structure)
                    </div>
                  ) : (
                    <div className="p-4 text-center text-gray-500">
                      No parameters found for this test
                    </div>
                  )}
                </div>
              );
            });
          }
          
          // No tests found
          return (
            <div className="p-8 text-center text-gray-500">
              No tests found in this order
            </div>
          );
        })()}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 justify-end pt-6 mt-6 border-t">
        {isModal ? (
          <button
            onClick={onClose}
            className="px-6 py-2 text-sm font-semibold text-white rounded-md bg-primary"
          >
            Close
          </button>
        ) : (
          <button
            onClick={() => navigate("/lab-order-list")}
            className="px-6 py-2 text-sm font-semibold bg-white rounded-md border"
          >
            Back
          </button>
        )}
      </div>

    </div>
  );
};

export default ViewLabOrder;
